import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, X, ShoppingBag, Heart, ShoppingCart, Calendar, Package} from 'lucide-react';
import { productService } from '../services/productService';
import { useAuth } from '../Context/AuthContext';
import Header from '../components/Header';
import './Gallery.css';

const Gallery = ({ addToCart, cartItems, savedItems, addFavorite, removeFavorite }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnPath, setReturnPath] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const location = useLocation();

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const result = await productService.getAllProducts();
        if (result.success) {
          const mappedProducts = result.data.map(p => ({
            id: p.id,
            title: p.name,
            category: p.category,
            price: `Rs ${p.price}`, // Placeholder original price for demonstration
            salePrice: `Rs ${p.sale_price}`,
            description: p.description,
            image: p.image_url,
            sizes: ["XS", "S", "M", "L", "XL", "XXL"]
          }));
          setDesigns(mappedProducts);
        } else {
          console.error("Failed to fetch products:", result.error);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, []);

  // Handle navigation from Favourites page to open a specific product modal
  useEffect(() => {
    if (location.state?.productId && designs.length > 0) {
      const design = designs.find(d => d.id.toString() === location.state.productId.toString());
      if (design) {
        setSelectedDesign(design);

        if (location.state.from) {
          setReturnPath(location.state.from);
        }

        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, designs]);

  const handleCloseModal = () => {
    setSelectedDesign(null);
    if (returnPath) {
      navigate(returnPath, { replace: true });
      setReturnPath(null);
    }
  };

  const categories = ['All', 'Design wear', 'Dresses', 'Blouses'];

  const filteredDesigns = selectedCategory === 'All'
    ? designs
    : designs.filter(design => design.category === selectedCategory);

  const handleSave = (design, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    if (isSaved(design.id)) {
      removeFavorite(design);
    } else {
      addFavorite(design);
    }
  };

  const handleBookConsultation = () => {
    navigate('/home', { state: { openAppointment: 'fitting' } });
  };

  const isSaved = (designId) => {
    return savedItems.some(item => item.product_id === designId);
  };

  const isInCart = (designId) => {
    return cartItems.some(item => item.id === designId);
  };

  const handleCartAction = (design) => {
    if (isInCart(design.id)) {
      navigate('/cart', { state: { from: '/gallery' } });
    } else {
      addToCart(design);
      showSuccessToast('Added to Cart!');
    }
  };

  const showSuccessToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage('');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="gallery-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: '#ec4899' }}>
          Loading Designs...
        </div>
      </div>
    )
  }

  return (
    <div className="gallery-container">
      {toastMessage && (
        <div className="cart-toast">
          <span className="toast-icon">✓</span>
          {toastMessage}
        </div>
      )}
      <Header
        leftAction={
          <button onClick={() => navigate(location.state?.from || '/home')} className="back-nav">
            <ArrowLeft size={20} />
            <span className="nav-text">Back</span>
          </button>
        }
        rightAction={
          <div className="header-actions">
            <button className="tracking-btn" onClick={() => navigate('/tracking')}>
                         <Package size={20} />
            </button>
            <button className="header-icon-btn" aria-label="Wishlist" onClick={() => navigate('/favourites', { state: { from: '/gallery', label: 'Gallery' } })}>
              <Heart size={20} />
              {savedItems.length > 0 && (
                <span className="heart-badge">{savedItems.length}</span>
              )}
            </button>
            <button className="header-icon-btn" aria-label="Cart" onClick={() => navigate('/cart', { state: { from: '/gallery' } })}>
              <ShoppingCart size={20} />
              {cartItems?.length > 0 && (
                <span className="cart-badge">{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
              )}
            </button>
          </div>
        }
      />

      <div className="gallery-content">
        <div className="gallery-title-section fade-in">
          <h1>Our Design Collection</h1>
          <p>Explore our exclusive range of handcrafted designs</p>
        </div>

        <div className="category-filters fade-in stagger-1">
          {categories.map(category => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="designs-grid">
          {filteredDesigns.map((design, index) => {
            const getThumbClass = (title) => {
              if (title === 'Short Top') return 'thumb-short-top';
              if (title === 'Umbrella Skirt with Top') return 'thumb-umbrella-up';
              if (title === 'Cinderella Gown') return 'thumb-cinderella-up';
              if (title === 'Designer Gown') return 'thumb-designer-gown-up';
              if (title === 'Princess cut with Puff Sleeve') return 'thumb-zoom-out';
              if (title === 'Sleeveless with Embroidery') return 'thumb-align-sleeve';
              if (title === 'Blouse without Sleeve') return 'thumb-blouse-sleeveless';
              return '';
            };

            const thumbClass = getThumbClass(design.title);
            const saved = isSaved(design.id);

            return (
              <div
                key={design.id}
                className={`design-card fade-in stagger-${(index % 4) + 2}`}
                onClick={() => setSelectedDesign(design)}
              >
                <div className="design-image">
                  <img
                    src={design.image}
                    alt={design.title}
                    className={thumbClass}
                  />
                  <div className="product-sale-badge">
                                                SALE
                                              </div>
                  <div className="design-hover-overlay">
                    <div className="design-actions">
                      <button
                        className={`btn-save ${saved ? 'active' : ''}`}
                        onClick={(e) => handleSave(design, e)}
                      >
                        <Heart size={18} fill={saved ? "currentColor" : "none"} />
                        {saved ? 'Saved' : 'Save'}
                      </button>
                      <button
                        className="btn-add-cart"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(design);
                          showSuccessToast('Added to Cart!');
                        }}
                      >
                        <ShoppingBag size={18} />
                        Add to Cart
                      </button>

                    </div>
                  </div>
                </div>
                <div className="design-info">
                  <h3 className="product-title">{design.title}</h3>
                    <div className="product-pricing">

                   <span className="original-price">{design.price}</span>
                  <span className="sale-price">{design.salePrice}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDesign && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content scale-in" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>
              <X size={24} />
            </button>
            <div className="modal-layout">
              <div className="modal-image">
                <img
                  src={selectedDesign.image}
                  alt={selectedDesign.title}
                  className={selectedDesign.image === '/images/designer-gown-1.jpg' ? 'modal-shift-up' : ''}
                />
              </div>
              <div className="modal-details">
                <h2>{selectedDesign.title}</h2>
                <p className="modal-price">{selectedDesign.price}</p>
                <p className="modal-description">{selectedDesign.description}</p>
                <div className="modal-features">
                  <h4>Features:</h4>
                  <ul>
                    <li>Premium quality fabrics</li>
                    <li>Complimentary alterations</li>
                  </ul>
                </div>
                {selectedDesign.sizes && (
                  <div className="modal-sizes fade-in">
                    <h4 className="animated-sizes-label">Available in All Sizes!</h4>
                    {/* <div className="size-tags">
                      {selectedDesign.sizes.map(size => (
                        <span key={size} className="size-tag">{size}</span>
                      ))}
                    </div> */}
                  </div>
                )}
                <div className="modal-note">
                  <span className="note-icon">💡</span>
                  <p><strong>Note:</strong> All designs can be customized to your measurements and preferences. Book a fitting session to get started!</p>
                </div>
                <div className="modal-buttons">
                  <button className="btn-book-consultation" onClick={handleBookConsultation}>
                    <Calendar size={20} />
                    Book Consultation
                  </button>
                  <button
                    className={`btn-add-favorites ${isSaved(selectedDesign.id) ? 'active' : ''}`}
                    onClick={(e) => handleSave(selectedDesign, e)}
                  >
                    <Heart size={20} fill={isSaved(selectedDesign.id) ? "currentColor" : "none"} />
                    {isSaved(selectedDesign.id) ? 'Saved' : 'Add to Favorites'}
                  </button>
                  <button
                    className={`btn-add-cart ${isInCart(selectedDesign.id) ? 'in-cart' : ''}`}
                    onClick={() => handleCartAction(selectedDesign)}
                  >
                    <ShoppingBag size={20} />
                    {isInCart(selectedDesign.id) ? 'Go to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;